import { PenLine, Plus, Trash2, Info } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMemo } from 'react'

export default function ManualQuestionForm({
  typeSummary,
  manualOptions,
  setManualOptions,
  manualCorrect,
  setManualCorrect,
  optionCount,
  typeSummaryMin,
  typeSummaryMax,
  newQuestionText,
  setNewQuestionText,
  addManualQuestion,
  openAlert,
  hideAddButton = false,
  hideAiButton = false
}) {
  const isSingle = typeSummary === 'TF'
  const inputType = isSingle ? 'radio' : 'checkbox'

  const toggleCorrect = idx => {
    const key = `option${idx + 1}`
    if (isSingle) {
      setManualCorrect([key])
    } else {
      setManualCorrect(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
    }
  }

  const rowVar = { hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }
  const answeredCnt = useMemo(() => manualOptions.filter(o => o.desc.trim()).length, [manualOptions])

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">ข้อความคำถาม</label>
        <div className="flex gap-2 flex-col md:flex-row">
          <textarea
            type="text"
            value={newQuestionText}
            onChange={e => setNewQuestionText(e.target.value)}
            className="border border-gray-300 rounded-xl px-4 py-2 text-sm flex-1 focus:ring-2 focus:ring-primary bg-white"
            placeholder="กรอกข้อความคำถาม"
            rows={3}
          />
          {!hideAiButton && (
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-secondary text-white rounded-xl px-4 py-2 text-sm"
              onClick={() => openAlert('ฟังก์ชันสร้างตัวเลือกด้วย AI ยังไม่เปิดใช้งาน')}
              title="สร้างตัวเลือกด้วย AI"
            >
              <PenLine size={14} /> สร้างตัวเลือกด้วย AI
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-500 inline-flex items-center gap-1">
          <Info size={12} /> แนะนำ: ตั้งคำถามให้เจาะจงและมีบริบทพอ เช่น เวลาช่วงใด, สาเหตุ, นิยาม ฯลฯ
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">
          ตัวเลือก <code>({manualOptions.length} / {optionCount})</code>{' '}
          <span className="text-gray-500 text-xs">กรอกแล้ว {answeredCnt}/{optionCount}</span>
        </label>

        <AnimatePresence initial={false}>
          {manualOptions.map((opt, i) => {
            const key = `option${i + 1}`
            const checked = manualCorrect.includes(key)
            return (
              <motion.div
                key={i}
                variants={rowVar}
                initial="hidden"
                animate="show"
                exit="hidden"
                transition={{ duration: 0.2 }}
                className="flex items-center gap-3 mb-2"
              >
                {typeSummary !== 'PO' && (
                  <input
                    type={inputType}
                    name="manual-correct" // group สำหรับ radio
                    checked={checked}
                    onChange={() => toggleCorrect(i)}
                    className="h-4 w-4 accent-primary"
                    title={isSingle ? 'เลือกคำตอบที่ถูกต้องเพียงข้อเดียว' : 'เลือกได้หลายข้อ'}
                  />
                )}
                <textarea
                  type="text"
                  placeholder={`ตัวเลือก ${i + 1}`}
                  value={opt.desc}
                  rows={2}
                  onChange={e => {
                    const copy = [...manualOptions]
                    copy[i].desc = e.target.value
                    setManualOptions(copy)
                  }}
                  className="border border-gray-300 rounded-xl px-3 py-2 text-sm flex-1 focus:ring-2 focus:ring-primary bg-white"
                />
                {typeSummary === 'PO' && (
                  <input
                    type="number"
                    min={typeSummaryMin}
                    max={typeSummaryMax}
                    step="1"
                    value={opt.points}
                    onChange={e => {
                      const copy = [...manualOptions]
                      copy[i].points = Number(e.target.value)
                      setManualOptions(copy)
                    }}
                    className="border border-gray-300 rounded-xl px-2 py-2 text-sm w-20 focus:ring-2 focus:ring-primary bg-white"
                    title={`กำหนดน้ำหนักระหว่าง ${typeSummaryMin}-${typeSummaryMax}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setManualOptions(prev => prev.filter((_, idx) => idx !== i))}
                  className="inline-flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-2 text-xs"
                  title="ลบตัวเลือกนี้"
                >
                  <Trash2 size={14} />
                </button>
              </motion.div>
            )
          })}
        </AnimatePresence>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (manualOptions.length < optionCount) setManualOptions(prev => [...prev, { desc: '', points: 1 }])
              else openAlert(`ตัวเลือกทั้งหมดจำกัดไว้ที่ ${optionCount}`)
            }}
            className="inline-flex items-center gap-2 bg-secondary hover:bg-primary text-white rounded-xl px-4 py-2 text-sm w-max mt-1"
          >
            <Plus size={16} /> เพิ่มตัวเลือก
          </button>
          {typeSummary !== 'PO' && (
            <span className="text-[11px] text-gray-500">
              {isSingle ? 'โหมดคำตอบถูกต้อง 1 ตัวเลือก (Single-choice)' : 'โหมดคำตอบถูกต้องได้หลายตัวเลือก (Multiple)'}
            </span>
          )}
        </div>

        {!hideAddButton && (
          <div className="mt-4">
            <button
              type="button"
              className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-xl px-5 py-2 text-sm"
              onClick={() => {
                if (!newQuestionText.trim()) {
                  openAlert('กรุณาป้อนข้อความคำถาม')
                  return
                }
                if (manualOptions.some(o => !o.desc.trim())) {
                  openAlert('กรุณาป้อนตัวเลือกให้ครบ')
                  return
                }
                if (manualCorrect.length === 0 && typeSummary !== 'PO') {
                  openAlert('เลือกคำตอบถูกต้องอย่างน้อย 1 ค่า')
                  return
                }
                addManualQuestion()
              }}
            >
              <PenLine size={14} /> เพิ่มข้อสอบ
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
